import React, {useState} from 'react';
import {useSelector} from "react-redux";

import './styles.scss';
import {
    Button,
    Box,
    AppBar,
    Toolbar,
    MenuItem,
    Menu,
    ListItemIcon,
    ListItemText,
    IconButton,
    SvgIcon,
    Divider
} from "@mui/material";
import MenuIcon from '@mui/icons-material/Menu';
import {keycloak} from "../../../keycloak-config";
import Logo from "../../../img/logo2x.png";
import {useGetUserDetailsQuery} from "../../../core/api/users-api";
import {NavLink} from "react-router-dom";

import { ReactComponent as ShopSvg } from "../../../img/shop.svg";
import { ReactComponent as BagSvg } from "../../../img/bag.svg";
import { ReactComponent as WalletSvg } from "../../../img/wallet.svg";
import { ReactComponent as ReceiptSvg } from "../../../img/receipt.svg";
import CampaignIcon from "@mui/icons-material/Campaign";

const ShopIcon = (props) => (
    <SvgIcon {...props} component={ShopSvg} viewBox="0 0 24 23" />
);
const BagIcon = (props) => (
    <SvgIcon {...props} sx={{width:25, height:30}} component={BagSvg} viewBox="0 0 24 26" />
);
const WalletIcon = (props) => (
    <SvgIcon {...props} sx={{width:25, height:30}}  component={WalletSvg} viewBox="0 0 28 23" />
);
const ReceiptIcon = (props) => (
    <SvgIcon {...props} sx={{width:25, height:35}}  component={ReceiptSvg} viewBox="-4 0 28 23" />
);

const Header = () => {
    useGetUserDetailsQuery();

    const sessionState = useSelector((state) => state.data.UserSession)
    const isUserHasAdminRole = sessionState.user.isAdmin;
    const isUserHasManagerRole = sessionState.user.isManager;

    const [anchorEl, setAnchorEl] = useState(null);
    const isMenuOpen = Boolean(anchorEl);
    const handleOpenMobileMenu = (event) => setAnchorEl(event.currentTarget);
    const handleCloseMobileMenu = () => setAnchorEl(null);

    const MAIN_TABS = [
        {
            label: "Shop",
            icon: <ShopIcon />,
            path: "/products"
        },
        {
            label: "Orders",
            icon: <BagIcon />,
            path: "/history"
        },
        {
            label: "Balance",
            icon: <WalletIcon />,
            path: "/balance"
        },
        {
            label: "Rules",
            icon: <ReceiptIcon />,
            path: "/rules/shop"
        },
        {
            label: "Accrual",
            icon: <span className={"menu__admin-h"}/>,
            path: "/request",
            visible: isUserHasManagerRole
        },
        {
            label: "I hosted an event",
            icon: <CampaignIcon />,
            // Admin renders the page inside the admin sidebar layout; Manager
            // (and anyone else with access) gets the standalone route.
            path: isUserHasAdminRole ? "/admin/experience/request/new" : "/experience/request/new",
            visible: isUserHasManagerRole || isUserHasAdminRole
        },
        {
            label: "Admin",
            icon: <span className={"menu__admin"}/>,
            path: "/admin/orderHistory",
            visible: isUserHasAdminRole
        },
    ]

    const visibleTabs = MAIN_TABS.filter(item => !item.hasOwnProperty('visible') || item.visible === true);

    let login
    if(sessionState.authenticated) {
        login = <Box sx={{display: 'flex'}}>
            <Button color="warning" variant="contained" onClick={keycloak.logout}>Log out</Button>
        </Box>
    } else {
        login = <Button color="warning" variant="contained" onClick={keycloak.login}>Log in</Button>
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <AppBar position="static" color="primary">
                <Toolbar sx={{ gap: { xs: 1, md: 2 } }}>
                    <NavLink to={"/"}>
                        <Box
                            component="img"
                            className={"main-logo"}
                            alt="Belcoin Shop"
                            src={Logo}
                        />
                    </NavLink>
                    <Box sx={{ flexGrow: 1 }} />

                    {sessionState.authenticated &&
                        <Box className={"header-menu"} sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'row' }}>
                            {visibleTabs.map(item => (
                                <IconButton
                                    component={NavLink}
                                    to={item.path}
                                    key={item.label}
                                    sx={{
                                        mx: 1,
                                        '&.active': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                        }
                                    }}
                                    title={item.label}
                                >
                                    {item.icon}
                                </IconButton>
                            ))}
                        </Box>
                    }

                    {sessionState.authenticated &&
                        <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                            <IconButton
                                aria-label="open menu"
                                aria-controls={isMenuOpen ? 'header-mobile-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={isMenuOpen ? 'true' : undefined}
                                onClick={handleOpenMobileMenu}
                                sx={{
                                    border: '1px solid #EF5226',
                                    borderRadius: '12px',
                                    color: '#EF5226'
                                }}
                            >
                                <MenuIcon />
                            </IconButton>
                            <Menu
                                id="header-mobile-menu"
                                anchorEl={anchorEl}
                                open={isMenuOpen}
                                onClose={handleCloseMobileMenu}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                PaperProps={{ className: "header-menu-mobile", sx: { mt: 1, minWidth: 220, borderRadius: 2 } }}
                            >
                                {visibleTabs.map((item, index) => [
                                    index > 0 && <Divider key={`divider-${item.label}`} component="li" sx={{ my: 0.5 }} />,
                                    <MenuItem
                                        key={item.label}
                                        component={NavLink}
                                        to={item.path}
                                        onClick={handleCloseMobileMenu}
                                        sx={{
                                            py: 1.25,
                                            '&.active': {
                                                backgroundColor: 'rgba(239, 82, 38, 0.12)',
                                                color: '#EF5226',
                                                fontWeight: 600
                                            }
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                                        <ListItemText primary={item.label} />
                                    </MenuItem>
                                ])}
                            </Menu>
                        </Box>
                    }

                    <Box sx={{ display: 'flex' }}>
                        {login}
                    </Box>
                </Toolbar>
            </AppBar>
        </Box>
    );
}

export default Header;
